import { authApi } from 'api';

/**
 * Get club athlete groups
 * @param {string} clubId - The club ID
 * @returns {Promise} - The response from the API
 */
export const getClubAthleteGroups = async (
  clubId,
  includeMembers = false,
  includeInactive = false
) => {
  try {
    const response = await authApi.get(
      `/Clubs/${clubId}/athlete-groups?includeMembers=${includeMembers}&includeInactive=${includeInactive}`
    );
    return response;
  } catch (err) {
    throw err;
  }
};

/**
 * Create a new club age group
 *  
 * @param {string} clubId - The club ID.
 * @param {Object} groupData - An object containing the details of the age group to be created.
 * @param {string} groupData.groupName - The name of the athlete group.
 * @param {string} groupData.groupType - The type of the athlete group
 * @param {string} groupData.baseAgeRange - base age range of the group
 * @param {number} groupData.sortOrder - The display order of the group.
 * @param {Array} groupData.athleteIds - The athlete IDs of the group.

 * @returns {Promise} - A promise resolving with the API response.
 */
export const createClubAthleteGroup = async (clubId, groupData) => {
  try {
    const response = await authApi.post(
      `/Clubs/${clubId}/athlete-groups`,
      groupData
    );
    return response;
  } catch (err) {
    throw err;
  }
};

/**
 * Get club athlete group by Id
 *
 * @param {string} clubId - The club ID.
 * @param {string} groupId - The athlete group ID.
 *
 * @returns {Promise} - A promise resolving with the API response.
 */
export const getClubAthleteGroupById = async (clubId, groupId) => {
  try {
    const response = await authApi.get(
      `/Clubs/${clubId}/athlete-groups/${groupId}`
    );
    return response;
  } catch (err) {
    throw err;
  }
};

/**
 * Update club athlete group by Id
 *
 * @param {string} clubId - The club ID.
 * @param {string} groupId - The age group ID.
 * @param {Object} groupData - An object containing the details of the age group to be created.
 * @param {string} groupData.groupName - The name of the athlete group.
 * @param {string} groupData.groupType - The type of the athlete group
 * @param {string} groupData.baseAgeRange - base age range of the group
 * @param {number} groupData.sortOrder - The display order of the group.
 * @param {Array} groupData.athleteIds - The athlete IDs of the group.
 * @returns {Promise} - A promise resolving with the API response.
 */
export const updateClubAthleteGroupById = async (
  clubId,
  groupId,
  groupData
) => {
  try {
    const response = await authApi.put(
      `/Clubs/${clubId}/athlete-groups/${groupId}`,
      groupData
    );
    return response;
  } catch (err) {
    throw err;
  }
};

/**
 * Delete club athlete group by Id
 *
 * @param {string} clubId - The club ID.
 * @param {string} groupId - The age group ID.
 *
 * @returns {Promise} - A promise resolving with the API response.
 */
export const deleteClubAthleteGroupById = async (clubId, groupId) => {
  try {
    const response = await authApi.delete(
      `/Clubs/${clubId}/athlete-groups/${groupId}`
    );
    return response;
  } catch (err) {
    throw err;
  }
};

/**
 * Replaces the entire member list of an athlete group.
 *
 * @param {string} clubId - The club ID.
 * @param {string} groupId - The athlete group ID.
 * @param {Object} athleteIds - An object containing an array of athlete IDs.
 * @returns {Promise} - A promise resolving with the API response.
 */
export const updateClubAthleteGroupMembers = async (
  clubId,
  groupId,
  athleteIds
) => {
  try {
    const response = await authApi.put(
      `/Clubs/${clubId}/athlete-groups/${groupId}/members`,
      athleteIds
    );
    return response;
  } catch (err) {
    throw err;
  }
};
